import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./Loading.module.scss";
import { BOOT_LOADER_COLOURS, bootLoaderMorphPosition, removeBootLoader } from "./bootLoaderUtility";
import { getLoaderMorph } from "./loaderMorphUtility";

// Less motion asked for: the shape still moves (a frozen loader reads as a hung page), only at half the pace.
const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

// Where the morph is (milliseconds into its cycle at the normal pace), shared by every loader on
// the page. The loader hides as soon as no request runs and shows again with the next one, often
// several times while a page opens; each one carries on from the frame the last one left.
let morphPosition: number | undefined;

export const Loading = () => {
  const morph = getLoaderMorph();
  const turnRef = useRef<HTMLDivElement>(null);
  const shapeRef = useRef<HTMLDivElement>(null);
  const [timing] = useState(() => {
    // The first loader carries on from index.html's boot loader. Without one it starts on a random
    // shape, already partway into its morph, so it moves at once.
    const bootPosition = bootLoaderMorphPosition();
    morphPosition ??=
      (bootPosition ??
        Math.floor(Math.random() * morph.shapes.length) * morph.stepMilliseconds + morph.leadInMilliseconds) %
      morph.cycleMilliseconds;
    return {
      pace: prefersReducedMotion() ? 2 : 1,
      position: morphPosition,
      shownAt: performance.now(),
      // Taking over from the boot loader, the mask is already there: fading it in again would flash the page.
      takesOverFromBoot: bootPosition !== undefined,
    };
  });

  // Starts the animations and takes the boot loader's place before the next paint, so the two
  // never show together.
  useLayoutEffect(() => {
    removeBootLoader();

    const turn = turnRef.current;
    const shape = shapeRef.current;
    if (timing.takesOverFromBoot && shape) {
      // The shape was drawn in the boot loader's colours. Once the browser has taken them as its
      // first style, they're let go, so the palette's (if it's already there) blend in instead.
      window.getComputedStyle(shape).getPropertyValue("--loader-from");
      shape.style.removeProperty("--loader-from");
      shape.style.removeProperty("--loader-to");
    }
    if (typeof turn?.animate !== "function" || typeof shape?.animate !== "function") return;

    const options: KeyframeAnimationOptions = {
      duration: morph.cycleMilliseconds * timing.pace,
      delay: -timing.position * timing.pace,
      iterations: Infinity,
    };
    const animations = [turn.animate(morph.turnKeyframes, options), shape.animate(morph.morphKeyframes, options)];

    return () => animations.forEach((animation) => animation.cancel());
  }, [morph, timing]);

  useEffect(
    () => () => {
      const shownFor = (performance.now() - timing.shownAt) / timing.pace;
      morphPosition = (timing.position + shownFor) % morph.cycleMilliseconds;
    },
    [timing, morph]
  );

  // Drawn until the animations take over: the nearest shape, so the first frame doesn't flash another one.
  const nearestStep = Math.round(timing.position / morph.stepMilliseconds) % morph.shapes.length;
  const shapeStyle = {
    clipPath: morph.shapes[nearestStep],
    ...(timing.takesOverFromBoot && { "--loader-from": BOOT_LOADER_COLOURS.from, "--loader-to": BOOT_LOADER_COLOURS.to }),
  } as CSSProperties;

  return (
    <div className={styles.loader_wrapper} role="status" style={timing.takesOverFromBoot ? { animation: "none" } : undefined}>
      <div className={styles.loader} aria-hidden="true">
        <div ref={turnRef} className={styles.loader_turn} style={{ transform: `rotate(${nearestStep * morph.stepTurn}deg)` }}>
          <div ref={shapeRef} className={styles.loader_shape} style={shapeStyle} />
        </div>
      </div>
      <div className={styles.loader_text}>Зареждане...</div>
    </div>
  );
};
